import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import Logger from "../../services/Logger";
import AccountService from "./AccountService";

function filterParams({ keyword, testAccountFilter, excludeStaffUser, excludeAppleStaff, appCode }) {
    const params = {};
    if (keyword) params.keyword = keyword;
    if (testAccountFilter === "exclude") params.testAccount = false;
    else if (testAccountFilter === "include") params.testAccount = true;
    if (excludeStaffUser) params.staff = false;
    if (excludeAppleStaff) params.excludeApplePrivateRelay = true;
    if (appCode) params.appCode = appCode;
    return params;
}

function eachDayInclusive(start, end) {
    const days = [];
    let cursor = start.startOf("day");
    const last = end.startOf("day");
    while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
        days.push(cursor);
        cursor = cursor.add(1, "day");
    }
    return days;
}

function signupCountsByDay(users) {
    const counts = new Map();
    (users || []).forEach((user) => {
        if (!user?.createdAt) return;
        const key = dayjs(Number(user.createdAt)).format("YYYY-MM-DD");
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
}

function DailySignupBarChart({ title, days, counts, tickFormat, height = 320 }) {
    const labels = days.map((day) => day.format(tickFormat));
    const values = days.map((day) => counts.get(day.format("YYYY-MM-DD")) || 0);
    const total = values.reduce((sum, n) => sum + n, 0);

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {total.toLocaleString()} signup{total === 1 ? "" : "s"} · x-axis is the calendar date
            </Typography>
            <BarChart
                height={height}
                grid={{ horizontal: true }}
                xAxis={[
                    {
                        scaleType: "band",
                        data: labels,
                        label: "Date",
                        tickLabelStyle: {
                            angle: labels.length > 10 ? -45 : 0,
                            textAnchor: labels.length > 10 ? "end" : "middle",
                            fontSize: 11,
                        },
                    },
                ]}
                yAxis={[{ label: "Signups", min: 0 }]}
                series={[{ data: values, label: "Signups", color: "#1976d2" }]}
            />
        </Box>
    );
}

export default function AccountSignupChart({
    keyword,
    testAccountFilter = "exclude",
    excludeStaffUser = false,
    excludeAppleStaff = false,
    appCode = "",
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await AccountService.signupTimeline(
                    filterParams({ keyword, testAccountFilter, excludeStaffUser, excludeAppleStaff, appCode }),
                );
                if (!cancelled) {
                    setUsers(Array.isArray(result?.users) ? result.users : []);
                }
            } catch (err) {
                Logger.error("Failed to load account signup chart:", err);
                if (!cancelled) {
                    setError("Failed to load signup chart.");
                    setUsers([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [keyword, testAccountFilter, excludeStaffUser, excludeAppleStaff, appCode]);

    const counts = useMemo(() => signupCountsByDay(users), [users]);
    const weekDays = useMemo(() => {
        const now = dayjs();
        return eachDayInclusive(now.startOf("week"), now.endOf("week"));
    }, []);
    const monthDays = useMemo(() => {
        const now = dayjs();
        return eachDayInclusive(now.startOf("month"), now.endOf("month"));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                {users.length.toLocaleString()} users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Excludes staff, Apple staff, and test accounts.
            </Typography>
            <DailySignupBarChart
                title="This week (daily)"
                days={weekDays}
                counts={counts}
                tickFormat="ddd M/D"
                height={280}
            />
            <DailySignupBarChart
                title="This month (daily)"
                days={monthDays}
                counts={counts}
                tickFormat="M/D"
                height={320}
            />
        </Box>
    );
}
